import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { getInterventionsAirbrake } from "../../utils";
import { DEFAULT_POLLING_INTERVAL } from "../../constants";
import { useIdle, useInterval } from "../../hooks";
import { EventEmitter } from "./EventEmitter";
import { fetchInterventions, setInterventionShown } from "./fetchers";
import type { InterventionMessage } from "../../types";

export type InterventionProps = {
  data?: InterventionMessage;
  onDismiss?: () => void;
  fireEvent?: (action: string) => void;
  duid?: string; // Not needed in React SSR pages, but required for HAML versions
};

export type InterventionsResponse = Record<string, InterventionMessage>;

export type WithInterventionProps<T> = T & InterventionProps;

export interface InterventionsProviderInterface {
  eventEmitter: EventEmitter<InterventionMessage>;
  duid?: string;
  site: string;
  vertical?: string;
  setShown: (interventionName: string) => Promise<boolean>;
  getInterventions: (
    eligibilityData?: Record<string, unknown>,
  ) => Promise<void>;
}

const InterventionsContext =
  createContext<InterventionsProviderInterface | null>(null);

type ProviderProps = {
  site: string;
  duid?: string;
  vertical?: string;
  inactivityTime?: number;
  disablePolling?: boolean;
  apiUrl?: string;
  children: ReactNode;
};

const idmAirbrake = getInterventionsAirbrake();

export const InterventionsProvider = ({
  site,
  duid,
  vertical,
  inactivityTime,
  disablePolling = false,
  apiUrl,
  children,
}: ProviderProps) => {
  const eventEmitter = useMemo(
    () => new EventEmitter<InterventionMessage>(),
    [],
  );

  // Process intervention messages and emit events
  const sendMessages = useCallback(
    (message: Record<string, InterventionMessage>) => {
      Object.entries(message).forEach(([name, intervention]) => {
        if (intervention.show) {
          eventEmitter.emit(name, intervention);
        }
      });
    },
    [eventEmitter],
  );

  // Interventions request handler
  const getInterventions = useCallback(
    async (eligibilityData?: Record<string, unknown>) => {
      try {
        const data = await fetchInterventions({
          duid: duid as string,
          site,
          vertical,
          apiUrl,
          eligibility_data: eligibilityData,
        });

        // Add listeners to event emitter
        sendMessages(data);
      } catch (err) {
        console.error(err);
        const error = err instanceof Error ? err : new Error(String(err));
        idmAirbrake.notify(error);
      }
    },
    [duid, sendMessages, site, vertical, apiUrl],
  );

  // If inactivityTime is set, track user activity
  useIdle({
    idleTime: inactivityTime ?? 0,
    onIdle: () => getInterventions({ inactive: true }),
    disable: disablePolling,
  });

  // Poll the interventions API
  useInterval(() => {
    if (duid && site && !disablePolling) {
      void getInterventions();
    }
  }, DEFAULT_POLLING_INTERVAL);

  // Set intervention as shown
  const setShown = useCallback(
    async (interventionName: string) => {
      const result = await setInterventionShown({
        duid: duid as string,
        interventionName,
        site,
        vertical,
      });
      return result;
    },
    [duid, site, vertical],
  );

  const value = useMemo(
    () => ({
      eventEmitter,
      duid,
      site,
      setShown,
      vertical,
      getInterventions,
    }),
    [eventEmitter, duid, site, setShown, vertical, getInterventions],
  );

  return (
    <InterventionsContext.Provider value={value}>
      {children}
    </InterventionsContext.Provider>
  );
};

export const useInterventions = () => {
  const context = useContext(InterventionsContext);

  if (!context) {
    throw new Error(
      "useInterventions must be used within an InterventionsProvider",
    );
  }
  return context;
};
